import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { AdBanner } from '../components/ads/AdBanner';
import { ADS_ENABLED } from '../config/ads';

const getT = (t: (k: string) => string, key: string, fallback: string) => {
  const v = t(key);
  return v === key ? fallback : v;
};

export function About() {
  const { t } = useTranslation();

  const values: { icon: string; title: string; body: string }[] = [
    {
      icon: 'lightbulb',
      title: getT(t, 'aboutPage.valueInnovation', 'Innovation'),
      body: getT(
        t,
        'aboutPage.valueInnovationDesc',
        'We relentlessly push the boundaries of what AI can do for career development, ensuring our users stay ahead of industry trends.',
      ),
    },
    {
      icon: 'favorite',
      title: getT(t, 'aboutPage.valueEmpathy', 'Empathy'),
      body: getT(
        t,
        'aboutPage.valueEmpathyDesc',
        'Job hunting is stressful. We design our tools to be supportive, encouraging, and human-centric at every touchpoint.',
      ),
    },
    {
      icon: 'public',
      title: getT(t, 'aboutPage.valueAccessibility', 'Accessibility'),
      body: getT(
        t,
        'aboutPage.valueAccessibilityDesc',
        "Premium interview prep shouldn't be a luxury. We strive to make world-class coaching accessible to everyone, everywhere.",
      ),
    },
  ];

  return (
    <>
      <SEO
        title="About Us | InterviewR"
        description="Learn how InterviewR combines AI precision with human expertise to help candidates land their dream roles."
        url={typeof window !== 'undefined' ? window.location.href : 'https://interview-r.com/about'}
      />

      <div className="min-h-screen bg-stitch-canvas text-stitch-fg antialiased">
        {/* Hero */}
        <section className="relative overflow-hidden bg-stitch-surface py-16 md:py-24">
          <div className="pointer-events-none absolute inset-0 z-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgb(var(--stitch-primary))_0%,transparent_50%)]" />
          </div>
          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="mb-6 text-4xl font-extrabold leading-tight tracking-tight text-stitch-fg md:text-6xl">
                {getT(t, 'aboutPage.heroTitleLine1', 'Empowering Your')}{' '}
                <span className="text-stitch-primary">{getT(t, 'aboutPage.heroHighlight', 'Career Journey')}</span>
              </h1>
              <p className="text-xl leading-relaxed text-stitch-muted">
                {getT(
                  t,
                  'aboutPage.heroSub',
                  'Born from the frustration of generic preparation, InterviewR was built to bridge the gap between candidate potential and professional success through cutting-edge AI and human expertise.',
                )}
              </p>
            </div>
          </div>
          <div className="relative z-10 mx-auto mt-10 max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {[
                {
                  icon: 'smart_toy',
                  title: getT(t, 'aboutPage.heroCardAiTitle', 'Realistic AI interviews'),
                  body: getT(
                    t,
                    'aboutPage.heroCardAiBody',
                    'Practice anytime with simulations modeled on real hiring loops.',
                  ),
                },
                {
                  icon: 'groups',
                  title: getT(t, 'aboutPage.heroCardExpertsTitle', 'Real human experts'),
                  body: getT(
                    t,
                    'aboutPage.heroCardExpertsBody',
                    'Book sessions with experienced interviewers for personal feedback.',
                  ),
                },
                {
                  icon: 'insights',
                  title: getT(t, 'aboutPage.heroCardFeedbackTitle', 'Feedback you can act on'),
                  body: getT(
                    t,
                    'aboutPage.heroCardFeedbackBody',
                    'Clear scoring and concrete next steps after every session.',
                  ),
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-8 shadow-stitch-card transition-shadow duration-300 hover:shadow-stitch-pop"
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-stitch-lg bg-stitch-primary-soft text-stitch-primary">
                    <span className="material-symbols-outlined">{card.icon}</span>
                  </div>
                  <h3 className="mb-2 text-lg font-bold text-stitch-fg">{card.title}</h3>
                  <p className="leading-relaxed text-stitch-muted">{card.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story bento */}
        <section className="bg-stitch-canvas py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 items-stretch gap-8 md:grid-cols-12">
              <div className="flex flex-col justify-center rounded-stitch-xl border border-stitch-border bg-stitch-surface p-8 md:col-span-7 md:p-12">
                <h2 className="mb-6 text-3xl font-bold text-stitch-fg">
                  {getT(t, 'aboutPage.storyTitle', 'Our Story')}
                </h2>
                <div className="space-y-4 text-lg leading-relaxed text-stitch-muted">
                  <p>
                    {getT(
                      t,
                      'aboutPage.storyP1',
                      'InterviewR began with a simple frustration: capable engineers, PMs, and designers were failing interviews not for lack of skill, but for lack of realistic practice that mirrors the real thing.',
                    )}
                  </p>
                  <p>
                    {getT(
                      t,
                      'aboutPage.storyP2',
                      'Traditional mock interviews were either too expensive or too generic. We envisioned a world where AI could provide the precision of an expert recruiter, available 24/7, to help everyone—from new grads to executive leaders—find their voice.',
                    )}
                  </p>
                  <p>
                    {getT(
                      t,
                      'aboutPage.storyP3',
                      'Today, InterviewR pairs realistic AI interview simulations with the option to book real, experienced interviewers — so you can practice on your own time and get human feedback when it counts.',
                    )}
                  </p>
                </div>
              </div>
              <div className="grid grid-rows-2 gap-8 md:col-span-5">
                <div className="relative flex flex-col justify-end overflow-hidden rounded-stitch-xl bg-stitch-primary p-8 text-white">
                  <span className="material-symbols-outlined absolute right-4 top-4 text-6xl opacity-20">psychology</span>
                  <h3 className="mb-2 text-2xl font-bold">
                    {getT(t, 'aboutPage.aiPrecisionTitle', 'AI Precision')}
                  </h3>
                  <p className="text-stitch-primary-soft/95">
                    {getT(
                      t,
                      'aboutPage.aiPrecisionBody',
                      'Every mock session is analyzed with models trained on real-world hiring rubrics.',
                    )}
                  </p>
                </div>
                <div className="relative flex flex-col justify-end overflow-hidden rounded-stitch-xl bg-stitch-accent p-8 text-white">
                  <span className="material-symbols-outlined absolute right-4 top-4 text-6xl opacity-20">groups</span>
                  <h3 className="mb-2 text-2xl font-bold">
                    {getT(t, 'aboutPage.expertInsightTitle', 'Expert Human Insight')}
                  </h3>
                  <p className="text-stitch-accent-soft/90">
                    {getT(
                      t,
                      'aboutPage.expertInsightBody',
                      'Connect with recruiters and mentors for nuanced, personal coaching.',
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission card */}
        <section className="py-10 md:py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-stitch-xl border-2 border-stitch-primary/20 bg-stitch-surface p-8 text-center shadow-stitch-pop md:p-16">
              <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-stitch-primary-soft text-stitch-primary">
                <span className="material-symbols-outlined text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                  rocket_launch
                </span>
              </div>
              <h2 className="mb-4 text-sm font-bold uppercase tracking-widest text-stitch-primary">
                {getT(t, 'aboutPage.missionKicker', 'Our Mission')}
              </h2>
              <blockquote className="mx-auto max-w-4xl text-2xl font-semibold leading-tight text-stitch-fg md:text-4xl">
                &ldquo;
                {getT(
                  t,
                  'aboutPage.missionQuote',
                  'To provide every candidate with the tools and confidence to land their dream job.',
                )}
                &rdquo;
              </blockquote>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="bg-stitch-surface py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-16 text-center">
              <h2 className="mb-4 text-3xl font-bold text-stitch-fg">
                {getT(t, 'aboutPage.valuesTitle', 'Our Values')}
              </h2>
              <p className="mx-auto max-w-2xl text-stitch-muted">
                {getT(
                  t,
                  'aboutPage.valuesSubtitle',
                  'The core principles that guide how we build our products and support our community.',
                )}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="rounded-stitch-xl border border-stitch-border bg-stitch-canvas p-8 transition-shadow duration-300 hover:shadow-stitch-pop"
                >
                  <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-stitch-lg border border-stitch-border bg-stitch-surface shadow-stitch-card">
                    <span className="material-symbols-outlined text-stitch-primary">{v.icon}</span>
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-stitch-fg">{v.title}</h3>
                  <p className="leading-relaxed text-stitch-muted">{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sponsored / inline ad */}
        {ADS_ENABLED && (
          <section className="bg-stitch-canvas py-10 md:py-12">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="relative overflow-hidden rounded-stitch-xl border-2 border-dashed border-stitch-border bg-stitch-surface-2 py-6">
                <AdBanner slot="about-inline" />
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="relative overflow-hidden bg-stitch-primary py-16 md:py-24">
          <div className="pointer-events-none absolute inset-0 opacity-10 [background-image:url('https://www.transparenttextures.com/patterns/cubes.png')]" />
          <div className="relative z-10 mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="mb-6 text-3xl font-bold text-white md:text-5xl">
              {getT(t, 'aboutPage.ctaTitle', 'Ready to master your next interview?')}
            </h2>
            <p className="mx-auto mb-10 max-w-2xl text-lg text-stitch-primary-soft md:text-xl">
              {getT(
                t,
                'aboutPage.ctaBody',
                'Run a free AI mock interview now, or book a session with a real interviewer when you want human feedback.',
              )}
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/register"
                className="rounded-stitch-lg bg-stitch-surface px-8 py-4 text-lg font-bold text-stitch-primary shadow-stitch-pop transition-all hover:bg-stitch-primary-soft active:scale-[0.98]"
              >
                {getT(t, 'aboutPage.ctaRegister', 'Register Now')}
              </Link>
              <Link
                to="/try-interview"
                className="rounded-stitch-lg border border-stitch-primary-soft/50 bg-stitch-primary-hover/30 px-8 py-4 text-lg font-bold text-white backdrop-blur-sm transition-all hover:bg-stitch-primary-hover/50 active:scale-[0.98]"
              >
                {getT(t, 'aboutPage.ctaTryAi', 'Try AI Free')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
